/**
 * Renders a table row for a project task, including all claim/cost/percentage fields and history.
 *
 * @param {Object}   props
 * @param {Object}   props.task - The task object, including cost, percentage, and claim info.
 * @param {Array}    props.progressClaims - Array of previous progress claim objects for the project.
 * @param {Object}   props.open - State for expanded/collapsed rows.
 * @param {Array}    props.selectedTask - Array of selected/edited task objects.
 * @param {Function} props.handleExpandIcon - Handler for expanding/collapsing the row.
 * @param {Function} props.handlePercentageChange - Handler for completed/remaining percentage changes.
 * @param {Function} props.handleClaimAmountChange - Handler for claim amount changes.
 * @param {Function} props.handleRemainingClaimAmountChange - Handler for claim remaining checkbox.
 * @param {string|number} [props.progressClaimId] - Optional: current progress claim id for context.
 *
 * Calculates totals for a task, including all subtasks and direct materials, and manages all input state and validation for claim/percentage fields.
 * Amount field is always the source of truth; percentage is calculated from amount for display purposes.
 * No auto-correction - users see exactly what they type.
 *
 * Use case: Used to display and edit claim/cost/percentage data for a single task in the invoice generation table, including all validation, rounding, and history display.
 */

import React, { Fragment, useEffect, useRef, useState } from "react";
import { Table, Collapse, Form } from "react-bootstrap";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import moment from "moment";
import idx from "idx";
import { numberFormat, toUSD } from "../../../../util/currency-formatter";
import _, { round } from "lodash";
import { disableArrows, parseNumericValue } from "../../../../util/common";
import { progressClaim } from "./utils/preview-budget/calculateTotal";
import {
  toDecimal,
  roundValue,
  Decimal,
} from "../../../../util/decimal-utils";

const RenderRowData = ({
  task = {},
  progressClaims = [],
  open = {},
  selectedTask = [],
  handleExpandIcon,
  handlePercentageChange,
  handleClaimAmountChange,
  handleRemainingClaimAmountChange,
  progressClaimId,
  childTaskIds,
}) => {
  // State for controlled inputs
  const [completedPercentageInput, setCompletedPercentageInput] = useState("");
  const [remainingPercentageInput, setRemainingPercentageInput] = useState("");
  const [remainingAmountInput, setRemainingAmountInput] = useState("");

  // Track user typing for each input
  const hasUserTypedCompleted = useRef(false);
  const hasUserTypedRemainingPercentage = useRef(false);
  const hasUserTypedRemainingAmount = useRef(false);

  // Track if we're showing preview values (for UX)
  const [showingPercentagePreview, setShowingPercentagePreview] =
    useState(false);
  const [showingAmountPreview, setShowingAmountPreview] = useState(false);

  // State for remaining amount display (real-time preview)
  const [remainingAmountDisplay, setRemainingAmountDisplay] = useState("");

  // Refs to store input elements for cursor positioning
  const remainingPercentageRef = useRef(null);
  const remainingAmountRef = useRef(null);

  // Track previous claimRemainingAmount value to detect checkbox changes
  const prevClaimRemainingAmount = useRef(task.claimRemainingAmount);

  // --- Progress claim history and task selection logic ---
  let prevProgress = [];
  let disable = true;

  // Handle section-level claims (look up by stageId instead of taskId)
  const isSectionClaim = task.type === 'SECTION';

  // Gather all previous progress claims for this task/section
  Array.isArray(progressClaims) &&
    progressClaims.forEach((claim) => {
      if (Array.isArray(claim.progressClaimTask)) {
        let prevProgressTask;
        if (isSectionClaim) {
          // For section claims, aggregate matching stageId OR childTaskIds
          const matchingTasks = claim.progressClaimTask.filter(
            (t) =>
              !t.isDelete &&
              (t.stageId === task.stageId ||
              t.stageId === task.id ||
              (childTaskIds && childTaskIds.includes(t.taskId)))
          );

          if (matchingTasks.length > 0) {
            // Aggregate history for section view

            // Deduplicate: If we have section-level claims (taskId null), use ONLY those
            // Otherwise use child task claims
            const sectionClaims = matchingTasks.filter(t => !t.taskId);
            const tasksToAggregate = sectionClaims.length > 0 ? sectionClaims : matchingTasks;

            prevProgressTask = {
              id: matchingTasks[0].id, // Use ID of first match
              progressClaimId: claim.u_id,
              taskId: task.id, // Section ID
              createdAt: claim.createdAt,
              // Sum up financial values
              total: tasksToAggregate.reduce(
                (sum, t) => sum + (parseFloat(t.total) || 0),
                0
              ),
              subTotal: tasksToAggregate.reduce(
                (sum, t) => sum + (parseFloat(t.subTotal) || 0),
                0
              ),
              percentageComplete:
                tasksToAggregate.reduce(
                  (sum, t) => sum + (parseFloat(t.percentageComplete) || 0),
                  0
                ) / (tasksToAggregate.length || 1), // Average % (approx)
            };
          }
        } else {
          // Standard task lookup by taskId
          prevProgressTask = claim.progressClaimTask.find(
            (t) => !t.isDelete && t.taskId === task.id
          );
        }
        prevProgressTask && prevProgress.push(prevProgressTask);
      }
    });

  // Sort previous progress claims by percentage complete (descending)
  prevProgress = _.orderBy(prevProgress, "percentageComplete", "desc");
  // If this task is selected, use the selected version and enable editing
  // For sections, look up by stageId
  const selectedIndex = isSectionClaim
    ? selectedTask.findIndex((st) => st.stageId === task.stageId || st.stageId === task.id)
    : selectedTask.findIndex((st) => st.id === task.id);
  if (selectedIndex > -1) {
    task = selectedTask[selectedIndex];
    disable = false;
  } else if (isSectionClaim) {
    // Section claims should always be enabled for input
    disable = false;
  }

  // --- Calculate total cost for this task (before claims) ---
  // Calculate the base cost without profit margin for claim calculations
  // For section claims, use the pre-calculated totalBefore (from stage summaries)
  const baseCost = isSectionClaim
    ? (parseFloat(task.totalBefore) || 0)
    : (task.allocatedMaterialCost ? parseFloat(task.allocatedMaterialCost) : 0) +
    (task.allocatedLaborCost ? parseFloat(task.allocatedLaborCost) : 0) +
    (task.allocatedSubContractorCost
      ? parseFloat(task.allocatedSubContractorCost)
      : 0);

  // Set totalBefore to base cost only (excluding profit margin)
  task.totalBefore = baseCost;

  // --- Calculate max claimable amount for this task (after previous claims) ---
  const previousClaimsAmount = progressClaim({
    task,
    progressClaims,
    progressClaimId,
    childTaskIds,
  });

  // Calculate max claimable percentage for this row
  // Prefer calculating from actual values to avoid stale state
  const calculatedPrevPercentage = task.totalBefore > 0
    ? (previousClaimsAmount / task.totalBefore) * 100
    : 0;

  // Update task's value to keep it in sync (optional but helpful for other logic)
  task.previousPercentageComplete = calculatedPrevPercentage;

  // Update total percentage complete to reflect the recalculated previous percentage
  // This ensures that when cost is corrected (from 0 to actual), the percentage calculates correctly (e.g. 100% instead of 0%)
  const currentRemaining = task.remainingPercentage || 0;
  task.percentageComplete = calculatedPrevPercentage + currentRemaining;

  const maxPercentage = 100 - calculatedPrevPercentage;
  const maxTotal = task.totalBefore - previousClaimsAmount;

  /**
   * Calculates the maximum claimable amount for input validation.
   * This excludes the current claim amount to avoid circular dependency.
   * Uses Decimal.js for exact precision calculations.
   *
   * @returns {number} Maximum claimable amount as a number
   *
   * @example
   * getMaxClaimableAmount(); // Returns: 12759.00
   */
  const getMaxClaimableAmount = () => {
    const taskTotal = task.totalBefore || 0;

    // Convert to Decimal for exact precision
    const taskTotalDecimal = toDecimal(Math.round(taskTotal * 100) / 100);

    // For section claims, use the section-specific previous claims calculation
    let totalPreviousClaims = new Decimal(0);
    if (isSectionClaim) {
      // Note: getSectionPreviousClaimsAmount is defined below, but we inline the logic here
      // to avoid circular dependency issues since it uses similar progressClaims lookup
      const sectionStageId = task.stageId || task.id;
      if (Array.isArray(progressClaims)) {
        progressClaims.forEach((claim) => {
          if (progressClaimId && claim.u_id === progressClaimId) {
            return;
          }
          if (Array.isArray(claim.progressClaimTask)) {
            // Get all relevant tasks for this section for this claim
            const matchingTasks = claim.progressClaimTask.filter((claimTask) =>
              !claimTask.isDelete &&
              (claimTask.stageId === sectionStageId ||
              (childTaskIds && childTaskIds.includes(claimTask.taskId)))
            );

            if (matchingTasks.length > 0) {
              // Check if we have a specific section-level claim (taskId is null/undefined)
              const sectionClaims = matchingTasks.filter(t => !t.taskId);

              if (sectionClaims.length > 0) {
                // If we have section-level claims, use those (prevents double counting)
                sectionClaims.forEach(t => {
                  const roundedClaimAmount = Math.round((t.total || 0) * 100) / 100;
                  totalPreviousClaims = totalPreviousClaims.plus(toDecimal(roundedClaimAmount));
                });
              } else {
                // Otherwise sum up child tasks
                matchingTasks.forEach(t => {
                  const roundedClaimAmount = Math.round((t.total || 0) * 100) / 100;
                  totalPreviousClaims = totalPreviousClaims.plus(toDecimal(roundedClaimAmount));
                });
              }
            }
          }
        });
      }
    } else {
      // Calculate sum of all previous claims EXCLUDING current claim being edited
      if (Array.isArray(progressClaims)) {
        progressClaims.forEach((claim) => {
          // Skip current claim when editing existing claim
          if (progressClaimId && claim.u_id === progressClaimId) {
            return;
          }

          if (Array.isArray(claim.progressClaimTask)) {
            const prevProgressTask = claim.progressClaimTask.find(
              (t) => !t.isDelete && t.taskId === task.id
            );
            if (prevProgressTask) {
              // Round to 2 decimal places to match display precision
              const roundedClaimAmount =
                Math.round((prevProgressTask.total || 0) * 100) / 100;
              const claimAmount = toDecimal(roundedClaimAmount);
              totalPreviousClaims = totalPreviousClaims.plus(claimAmount);
            }
          }
        });
      }
    }

    // Calculate max claimable: Total - All Previous Claims (NOT including current)
    const maxClaimableValue = taskTotalDecimal.minus(totalPreviousClaims);

    // Round to 2 decimal places using Decimal.js
    const roundedMax = roundValue(maxClaimableValue, 2);

    // Guard against negative values
    return roundedMax <= 0 ? 0 : roundedMax;
  };

  /**
   * For section claims: calculates sum of all previous task-level claims under this section.
   * This aggregates historical claims from child tasks to show in "Previously Claimed" column.
   * 
   * @returns {number} Total previously claimed amount for the section
   */
  const getSectionPreviousClaimsAmount = () => {
    if (!isSectionClaim) return 0;

    let totalPreviousClaims = new Decimal(0);
    const sectionStageId = task.stageId || task.id;

    if (Array.isArray(progressClaims)) {
      progressClaims.forEach((claim) => {
        // Skip current claim when editing existing claim
        if (progressClaimId && claim.u_id === progressClaimId) {
          return;
        }

        // Get all relevant tasks for this section for this claim
        const matchingTasks = claim.progressClaimTask.filter((claimTask) =>
          !claimTask.isDelete &&
          (claimTask.stageId === sectionStageId ||
          (childTaskIds && childTaskIds.includes(claimTask.taskId)))
        );

        if (matchingTasks.length > 0) {
          // Check if we have a specific section-level claim (taskId is null/undefined)
          const sectionClaims = matchingTasks.filter(t => !t.taskId);

          if (sectionClaims.length > 0) {
            // If we have section-level claims, use those (prevents double counting)
            sectionClaims.forEach(t => {
              const roundedClaimAmount = Math.round((t.total || 0) * 100) / 100;
              totalPreviousClaims = totalPreviousClaims.plus(toDecimal(roundedClaimAmount));
            });
          } else {
            // Otherwise sum up child tasks
            matchingTasks.forEach(t => {
              const roundedClaimAmount = Math.round((t.total || 0) * 100) / 100;
              totalPreviousClaims = totalPreviousClaims.plus(toDecimal(roundedClaimAmount));
            });
          }
        }
      });
    }

    return roundValue(totalPreviousClaims, 2);
  };

  /**
   * Calculates the raw remaining claimable amount (as number) for display purposes.
   * Uses Decimal.js for exact precision calculations.
   * For section claims, aggregates child task claims.
   *
   * @returns {number} Raw remaining amount as a number
   *
   * @example
   * getRawRemainingAmount(); // Returns: 1421.52
   */
  const getRawRemainingAmount = () => {
    const taskTotal = task.totalBefore || 0;
    const currentClaimAmount = task.remainingAmount || 0;

    // Convert to Decimal for exact precision - avoid parseFloat to prevent floating-point errors
    // Round to 2 decimal places to match display precision and avoid database precision issues
    const taskTotalDecimal = toDecimal(Math.round(taskTotal * 100) / 100);
    const currentClaimAmountDecimal = toDecimal(
      Math.round(currentClaimAmount * 100) / 100
    );

    // For section claims, use the section-specific previous claims calculation
    let totalPreviousClaims = new Decimal(0);
    if (isSectionClaim) {
      totalPreviousClaims = toDecimal(getSectionPreviousClaimsAmount());
    } else {
      // Calculate sum of all previous claims from progress claims history
      // Exclude current claim when editing existing claim to avoid double counting
      if (Array.isArray(progressClaims)) {
        progressClaims.forEach((claim) => {
          // Skip current claim when editing existing claim
          if (progressClaimId && claim.u_id === progressClaimId) {
            return;
          }

          if (Array.isArray(claim.progressClaimTask)) {
            const prevProgressTask = claim.progressClaimTask.find(
              (t) => !t.isDelete && t.taskId === task.id
            );
            if (prevProgressTask) {
              // Round to 2 decimal places to match display precision
              const roundedClaimAmount =
                Math.round((prevProgressTask.total || 0) * 100) / 100;
              const claimAmount = toDecimal(roundedClaimAmount);
              totalPreviousClaims = totalPreviousClaims.plus(claimAmount);
            }
          }
        });
      }
    }

    // Calculate remaining amount: Total - All Previous Claims - Current Claim
    const remainingAmountValue = taskTotalDecimal
      .minus(totalPreviousClaims)
      .minus(currentClaimAmountDecimal);

    // Round to 2 decimal places for display using Decimal.js
    const roundedRemaining = roundValue(remainingAmountValue, 2);

    // Guard against negative values
    return roundedRemaining <= 0 ? 0 : roundedRemaining;
  };

  /**
   * Calculates the remaining claimable amount for this task.
   * Uses Decimal.js for exact precision calculations.
   *
   * @returns {string} Formatted remaining amount with proper currency formatting
   *
   * @description
   * Calculates remaining amount by:
   * 1. Getting total task cost from task.totalBefore
   * 2. Summing all previous claims from progress claims history
   * 3. Subtracting current claim amount (if any)
   * 4. Using Decimal.js for exact precision throughout
   *
   * @example
   * getRemainingAmount(); // Returns: "$1,421.52"
   */
  const getRemainingAmount = () => {
    // If showing preview while typing in claim amount, return preview value
    if (remainingAmountDisplay) {
      return remainingAmountDisplay;
    }

    const roundedRemaining = getRawRemainingAmount();

    // Guard against negative values
    if (roundedRemaining <= 0) {
      return "$0.00";
    } else {
      // Use toUSD for consistent formatting without parseFloat precision issues
      return toUSD(roundedRemaining);
    }
  };

  // --- Reset typing flags when checkbox is toggled ---
  useEffect(() => {
    // Detect if claimRemainingAmount checkbox was toggled
    if (prevClaimRemainingAmount.current !== task.claimRemainingAmount) {
      // Reset all typing flags so inputs can update
      hasUserTypedRemainingPercentage.current = false;
      hasUserTypedRemainingAmount.current = false;

      // Clear preview states
      setShowingPercentagePreview(false);
      setShowingAmountPreview(false);
      setRemainingAmountDisplay("");

      // Update ref for next comparison
      prevClaimRemainingAmount.current = task.claimRemainingAmount;
    }
  }, [task.claimRemainingAmount]);

  // --- Set initial values for all controlled inputs (with two decimals) ---
  useEffect(() => {
    if (!hasUserTypedCompleted.current) {
      // Function to sanitize and cap the task completion percentage
      const filteredPreviousCompleted = () => {
        // If percentage is extremely close to 100%, round it up to 100%
        if (task.percentageComplete >= 99.9999) {
          return 100;
        }
        // Prevent negative completion percentages. TODO: This should be removed once we fix the backend to prevent negative percentages
        else if (task.percentageComplete < 0) {
          return 0;
        }
        // Return the original value if it's within valid range
        else {
          return task.percentageComplete;
        }
      };

      const formatted = roundValue(
        filteredPreviousCompleted(), 2
      );
      setCompletedPercentageInput(formatted);
    }
    if (!hasUserTypedRemainingPercentage.current && !showingPercentagePreview) {
      // Always calculate percentage from amount (amount is source of truth)
      const calculatedPercentage =
        task.totalBefore > 0
          ? (task.remainingAmount / task.totalBefore) * 100
          : 0;
      const formatted = roundValue(calculatedPercentage, 2);
      setRemainingPercentageInput(formatted);
    }
    if (!hasUserTypedRemainingAmount.current && !showingAmountPreview) {
      // Always update amount field (amount is source of truth)
      const formatted = roundValue(task.remainingAmount, 2);
      setRemainingAmountInput(formatted);
    }
  }, [
    task.percentageComplete,
    task.remainingPercentage,
    task.totalBefore,
    task.remainingAmount,
    task.id,
    showingPercentagePreview,
    showingAmountPreview,
  ]);

  // --- Handlers for all input changes and blurs (with cursor preservation and validation) ---

  /**
   * Handles user input for remaining percentage field.
   * Preserves cursor, clamps value based on actual remaining dollar amount, and triggers parent handler.
   * Shows preview of calculated amount for better UX.
   * Restricts input to maximum 2 decimal places and clamps based on remaining amount (not percentage).
   */
  const handleRemainingPercentageChange = (e) => {
    hasUserTypedRemainingPercentage.current = true;
    const input = e.target;
    const oldValue = input.value;
    let newValue = input.value;
    const selectionStart = input.selectionStart;

    // Restrict to 2 decimal places maximum
    if (/^[0-9]*\.?[0-9]{0,2}$/.test(newValue)) {
      let wasClamped = false;
      let finalAmount = 0;

      // Validate and clamp based on maximum claimable dollar amount (amount is source of truth)
      if (newValue && !isNaN(parseFloat(newValue))) {
        const percentage = parseFloat(newValue);
        const previousCompleted = task.previousPercentageComplete || 0;
        const newCompletedPercentage = previousCompleted + percentage;

        // Calculate dollar amount from typed percentage
        let calculatedAmount = (task.totalBefore || 0) * (percentage / 100);

        // Get maximum claimable amount (excluding current claim to avoid circular dependency)
        const maxClaimableAmount = getMaxClaimableAmount();

        // If this would result in ~100% completion (within 0.01%), use exact remaining amount
        // This prevents showing 100% complete when there's still a few cents left due to rounding
        const isNearComplete = newCompletedPercentage >= 99.99;

        if (isNearComplete && Math.abs(calculatedAmount - maxClaimableAmount) < maxClaimableAmount * 0.01) {
          // Use exact remaining amount to ensure we claim everything
          calculatedAmount = maxClaimableAmount;
          wasClamped = true;

          // Keep the user's typed percentage as-is (don't recalculate)
          // This allows them to see 2.29% → $2,164.57 (exact remaining)
        } else if (calculatedAmount > maxClaimableAmount) {
          // If calculated amount exceeds max claimable, clamp to max claimable
          calculatedAmount = maxClaimableAmount;
          wasClamped = true;

          // Calculate percentage back from clamped amount
          const clampedPercentageValue = task.totalBefore > 0
            ? (maxClaimableAmount / task.totalBefore) * 100
            : 0;

          // Format to 2 decimal places when clamping
          newValue = parseNumericValue(numberFormat(clampedPercentageValue, true));

          // Adjust cursor position if we clamped the value
          setTimeout(() => {
            input.setSelectionRange(newValue.length, newValue.length);
          }, 0);
        }

        finalAmount = calculatedAmount;
      }

      // Calculate new cursor position (only if we didn't clamp)
      if (!wasClamped && newValue === input.value) {
        let newCursorPosition = selectionStart;
        if (newValue.length < oldValue.length) newCursorPosition = selectionStart;
        else if (newValue.length > oldValue.length)
          newCursorPosition =
            selectionStart + (newValue.length - oldValue.length);

        // Preserve cursor position
        setTimeout(() => {
          input.setSelectionRange(newCursorPosition, newCursorPosition);
        }, 0);
      }

      setRemainingPercentageInput(newValue);

      // Show preview of calculated amount for UX
      if (newValue && !isNaN(parseFloat(newValue))) {
        const percentage = parseFloat(newValue);
        const amount = wasClamped ? finalAmount : (task.totalBefore || 0) * (percentage / 100);
        const formattedAmount = roundValue(amount, 2);
        setRemainingAmountInput(formattedAmount);
        setShowingAmountPreview(true);
      }

      // If clamped, use amount handler with exact amount; otherwise use percentage handler
      if (wasClamped) {
        handleClaimAmountChange({ target: { value: finalAmount.toString() } }, task.id);
      } else {
        handlePercentageChange({ target: { value: newValue } }, task.id);
      }
    }
  };

  /**
   * Handles user input for remaining claim amount field.
   * Preserves cursor, validates against maximum, and triggers parent handler.
   */
  const handleRemainingAmountChange = (e) => {
    hasUserTypedRemainingAmount.current = true;
    const input = e.target;
    const oldValue = input.value;
    let newValue = input.value;
    const selectionStart = input.selectionStart;

    // Strip commas from the value to allow editing formatted numbers
    const cleanValue = newValue.replace(/,/g, '');

    // Restrict to 2 decimal places maximum (validate clean value without commas)
    if (/^[0-9]*\.?[0-9]{0,2}$/.test(cleanValue)) {
      // Calculate new cursor position
      let newCursorPosition = selectionStart;
      if (newValue.length < oldValue.length)
        newCursorPosition = selectionStart; // Backspace or delete
      else if (newValue.length > oldValue.length)
        newCursorPosition =
          selectionStart + (newValue.length - oldValue.length); // Typing

      // Preserve cursor position
      setTimeout(() => {
        input.setSelectionRange(newCursorPosition, newCursorPosition);
      }, 0);

      // Validate against maximum allowed amount
      let maxRemaining =
        task.totalBefore -
        progressClaim({ task, progressClaims, progressClaimId });

      if (progressClaimId) {
        // For existing claims, allow the user to enter the full remaining amount
        // The system will handle any overages during save/submission
        maxRemaining =
          task.totalBefore -
          progressClaim({ task, progressClaims, progressClaimId });
        // Only block if significantly over the limit (more than $1 over)
        if (parseFloat(cleanValue) > maxRemaining + 1.0) return;
      } else {
        maxRemaining = round(maxRemaining, 2);
        // Only clamp if significantly over the limit (more than $1 over)
        if (maxRemaining + 1.0 < Number(cleanValue)) {
          newValue = maxRemaining;
        }
      }

      // Store clean value without commas
      setRemainingAmountInput(cleanValue);

      setShowingAmountPreview(false);

      // Update percentage displays in real-time for UX preview (without calling parent)
      if (cleanValue && !isNaN(parseFloat(cleanValue))) {
        const amount = parseFloat(cleanValue);
        if (task.totalBefore > 0) {
          // Calculate and show claim percentage
          const calculatedPercentage = (amount / task.totalBefore) * 100;
          const formattedPercentage = roundValue(calculatedPercentage, 2);
          setRemainingPercentageInput(formattedPercentage);

          // Calculate and show completed percentage preview
          const previousCompleted = task.previousPercentageComplete || 0;
          const newCompletedPercentage = previousCompleted + calculatedPercentage;
          const formattedCompleted = roundValue(newCompletedPercentage, 2);
          setCompletedPercentageInput(formattedCompleted);

          // Calculate and show remaining amount preview
          const maxClaimable = getMaxClaimableAmount();
          const newRemaining = Math.max(0, maxClaimable - amount);
          setRemainingAmountDisplay(toUSD(newRemaining));
        }
      } else {
        // If empty, reset to original remaining amount
        setRemainingAmountDisplay("");
      }

      // Don't trigger parent on every keystroke - wait for blur to prevent multiple updates
      // The blur handler will call the parent once when user is done typing
    }
  };

  /**
   * Handles blur event for remaining claim amount field.
   * Formats the display value and triggers parent handler once when user is done typing.
   */
  const handleRemainingAmountBlur = (e) => {
    // Only process if user actually typed in this field
    if (!hasUserTypedRemainingAmount.current) {
      return;
    }

    let raw = parseFloat(parseNumericValue(e.target.value));
    if (!isNaN(raw)) {
      // Format the value for display
      const formatted = roundValue(raw, 2);
      setRemainingAmountInput(formatted);

      // Update the percentage display based on the formatted amount
      setShowingPercentagePreview(false);
      if (task.totalBefore > 0) {
        const calculatedPercentage = (raw / task.totalBefore) * 100;
        const formattedPercentage =
          roundValue(calculatedPercentage, 2);
        setRemainingPercentageInput(formattedPercentage);
      }

      // Clear remaining amount preview and revert to actual calculation
      setRemainingAmountDisplay("");

      // Call parent handler once when done typing
      handleClaimAmountChange(
        { target: { value: raw.toString() } },
        task.id
      );
    } else {
      // If invalid, reset to 0
      const fallback = roundValue(0, 2);
      setRemainingAmountInput(fallback);
      setShowingPercentagePreview(false);
      setRemainingPercentageInput("0.00");
      setRemainingAmountDisplay("");
      handleClaimAmountChange({ target: { value: "0" } }, task.id);
    }

    hasUserTypedRemainingAmount.current = false;
  };

  /**
   * Handles focus event for input fields.
   * Selects all text for easy replacement.
   */
  const handleInputFocus = (e) => {
    e.target.select();
  };

  // --- Utility: Is this task fully claimed? ---
  const isFullyClaimed = previousClaimsAmount >= task.totalBefore;
  return (
    <Fragment key={task.id}>
      <tr>
        <td className="expand-icon">
          <ExpandMoreIcon
            onClick={() => handleExpandIcon(task, open)}
            aria-controls="example-collapse-text"
            aria-expanded={open[task.id]}
          />
        </td>
        <td>{task.newName}</td>

        {/* Output value for Cost */}
        <td className="text-right">{toUSD(task.totalBefore)}</td>
        <td className="text-right">
          {task.subTasks && task.subTasks.length < 0 ? (
            ""
          ) : (
            <span className="text-right">{completedPercentageInput} %</span>
          )}
        </td>
        {/* Output text for Remaining Amount */}
        <td className="text-right">{getRemainingAmount()}</td>

        {/* Input field for Claim % */}
        <td>
          {task.subTasks && task.subTasks.length < 0 ? (
            ""
          ) : (
            <div className="input-block input-block-sm d-flex">
              <input
                ref={remainingPercentageRef}
                data-cy="remaining-percentage"
                className="form-control-input text-right min-w-150"
                type="text"
                inputMode="decimal"
                step="0.01"
                max={maxPercentage}
                value={remainingPercentageInput}
                disabled={disable || isFullyClaimed}
                onFocus={handleInputFocus}
                onChange={handleRemainingPercentageChange}
                onKeyDown={disableArrows}
              />
              <span className="my-auto ms-2">%</span>
            </div>
          )}
        </td>

        {/* Input for Claim Amount */}
        <td>
          {task.subTasks && task.subTasks.length < 0 ? (
            ""
          ) : (
            <div className="input-block input-block-sm">
              <input
                ref={remainingAmountRef}
                data-cy="remaining-amount"
                className="form-control-input text-right min-w-150"
                type="text"
                inputMode="decimal"
                step="0.01"
                min={0}
                max={maxTotal}
                value={remainingAmountInput}
                disabled={disable || isFullyClaimed}
                onFocus={handleInputFocus}
                onChange={handleRemainingAmountChange}
                onBlur={handleRemainingAmountBlur}
                onKeyDown={disableArrows}
              />
            </div>
          )}
        </td>

        {/* Checkbox for Claim Remaining */}
        <td>
          <div className="table-checkbox">
            <Form.Check
              type="checkbox"
              data-cy="claim-remaining-amount"
              checked={task.claimRemainingAmount || false}
              onChange={(e) => handleRemainingClaimAmountChange(e, task.id)}
              disabled={isFullyClaimed}
            />
          </div>
        </td>
      </tr>

      <tr>
        <td colSpan={8} className="p-0 border-0">
          <Collapse in={open[task.id]}>
            <div id="example-collapse-text" className="history-table">
              <h3>History</h3>
              <div className="table-responsive">
                <Table
                  bordered
                  className="table-create table-material table-material-ch"
                >
                  <thead>
                    <tr>
                      <th className="w-auto">Progress Claim Id</th>
                      <th className="w-auto">Date</th>
                      <th className="text-right">Total Amount</th>
                      <th className="text-right">Claim Percentage</th>
                      <th className="text-right">Claim Amount ($)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(prevProgress) && prevProgress.length ? (
                      prevProgress.map((historyRow) => {
                        const taskProgressClaimIds = [];
                        if (progressClaimId) {
                          progressClaims.forEach((claim) => {
                            if (progressClaimId === claim.u_id) {
                              claim.progressClaimTask.forEach((task) =>
                                taskProgressClaimIds.push(task.progressClaimId)
                              );
                            }
                          });
                        }
                        if (
                          taskProgressClaimIds.includes(
                            historyRow.progressClaimId
                          )
                        ) {
                          return null;
                        }
                        return (
                          <tr key={historyRow.id}>
                            <td>{historyRow.id}</td>
                            <td>
                              {moment(historyRow.createdAt).format(
                                "DD-MM-YYYY"
                              )}
                            </td>
                            <td className="text-right">
                              {numberFormat(
                                historyRow.subTotal
                                  ? parseFloat(historyRow.subTotal)
                                  : 0
                              )}
                            </td>
                            <td className="text-right">
                              {idx(historyRow, (_) => _.percentageComplete)
                                ? `${(
                                  Math.round(
                                    historyRow.percentageComplete * 100
                                  ) / 100
                                )?.toFixed(2) || 100
                                }%`
                                : "100%"}
                            </td>
                            <td className="text-right">
                              {numberFormat(parseFloat(historyRow.total)) ||
                                "0.00"}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={5} className="text-center">
                          {"No History to Show"}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </div>
          </Collapse>
        </td>
      </tr>
    </Fragment>
  );
};

export default RenderRowData;
